import React, { useEffect, useState } from 'react';
import { Tag } from 'antd';
import { KeyValueSelect } from '@shared/components';
import styled from 'styled-components';
import { useGetTagValues } from '@src/services/query/applang/queryHooks';
import { useDimensions } from '@src/services/queries';
import type { FilterOperator } from '@src/shared/types/user-journeys';
import { useLatest } from '@shared/hooks';

const DimensionFilterContainer = styled.div<{ $invalid?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  border: ${(props) => (props.$invalid ? '1px solid #ff4d4f' : 'none')};
`;

export interface DimensionFilterProps {
  dimensionKey?: string;
  dimensionValues?: { value: string; label?: string }[];
  dimensionOperator?: FilterOperator;
  interval: [string, string] | undefined;
  onDimensionKeyChange: (key: string) => void;
  onDimensionValuesChange: (
    values: { value: string; label: string }[],
    operator: FilterOperator,
  ) => void;
  loading?: boolean;
  onDelete?: () => void;
}

const DimensionFilter: React.FC<DimensionFilterProps> = ({
  dimensionKey,
  dimensionValues = [],
  dimensionOperator,
  interval,
  onDimensionKeyChange,
  onDimensionValuesChange,
  loading = false,
  onDelete,
}) => {
  const [searchValue, setSearchValue] = useState<string>('');
  const [debouncedSearchKey, setDebouncedSearchKey] = useState<string>('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchKey(searchValue);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [searchValue]);

  // Tag values state
  const [dimensionValueOptions, setDimensionValueOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);

  const { data: tagValues, isFetching: isLoadingValues } = useGetTagValues(
    {
      dimension_id: dimensionKey || '',
      tag_key: dimensionKey || '',
      time_range: interval,
      // use October 2025 as time range mock
      // time_range: ['2025-09-13T23:00:00.000Z', '2025-10-13T23:00:00.000Z'],
      key: debouncedSearchKey,
    },
    {
      enabled: isDropdownOpen, // Only fetch when dropdown is open
    },
  );
  const [localValues, setLocalValues] =
    useState<{ value: string; label?: string }[]>(dimensionValues);
  const [localOperator, setLocalOperator] = useState<FilterOperator>(dimensionOperator || 'equals');

  // Use refs to always access the latest values in callbacks (avoids stale closure)
  const localValuesRef = useLatest(localValues);
  const localOperatorRef = useLatest(localOperator);

  const { isLoading: isDimensionsLoading, data: dimensions } = useDimensions();

  // Process tag values data when it changes
  useEffect(() => {
    if (tagValues && Array.isArray(tagValues)) {
      const options = tagValues.map((value) => ({
        label: value.label,
        value: value.val,
      }));
      setDimensionValueOptions(options);
    } else {
      const curOptions = dimensionValues.map((value) => ({
        label: value.label || value.value,
        value: value.value,
      }));
      setDimensionValueOptions(curOptions || []);
    }
  }, [tagValues]);

  const handleKeyValueChange = (
    key: string | undefined,
    values: (string | { value: string; label?: React.ReactNode })[],
    operator?: string,
  ) => {
    const filterOperator = (operator || 'equals') as FilterOperator;

    const keyChanged = key !== dimensionKey;
    const operatorChanged = filterOperator !== localOperator;

    if (keyChanged || operatorChanged) {
      setSearchValue('');
      if (key) {
        onDimensionKeyChange(key);
      }
      const valuesWithLabels = localValues.map((v) => ({
        value: v.value,
        label: v.label || v.value,
      }));
      // reset values when key changes
      onDimensionValuesChange(keyChanged ? [] : valuesWithLabels, filterOperator);
    }

    // Convert values to internal format
    const normalizedValues = values.map((v) => {
      if (typeof v === 'string') {
        return { value: v, label: v };
      }
      return { value: v.value, label: typeof v.label === 'string' ? v.label : v.value };
    });
    setLocalValues(normalizedValues);
    setLocalOperator(filterOperator);
  };

  const handleBlur = () => {
    // Read from refs to get the latest values (avoids stale closure issue)
    const currentValues = localValuesRef.current;
    const currentOperator = localOperatorRef.current;
    // Ensure label is always a string
    const valuesWithLabels = currentValues.map((v) => ({
      value: v.value,
      label: v.label || v.value,
    }));
    onDimensionValuesChange(valuesWithLabels, currentOperator);
  };

  const handleSearch = (key: string | undefined, values: string[], _operator?: string) => {
    setSearchValue(values.join(''));
  };

  const handleDropdownVisibleChange = (open: boolean) => {
    setIsDropdownOpen(open);
  };

  const isValidDimension =
    !dimensionKey || !dimensions || !!dimensions.dimensionMap.get(dimensionKey);

  return (
    <DimensionFilterContainer $invalid={!isValidDimension}>
      <KeyValueSelect
        labelInValue
        disableKey={!isValidDimension}
        disableOperator={!isValidDimension}
        disableValues={!isValidDimension}
        keyOptions={dimensions?.dimensionOptions || []}
        valueOptions={dimensionValueOptions}
        selectedKey={dimensionKey}
        selectedValues={localValues}
        selectedOperator={localOperator}
        loadingKey={isDimensionsLoading || loading}
        loadingValues={isLoadingValues || loading}
        onChange={handleKeyValueChange}
        onBlur={handleBlur}
        getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
        onDelete={onDelete}
        keyPlaceholder="Dimension..."
        valuePlaceholder="Value..."
        invalidKeyPlaceholder={<Tag color="red">Dimension Deleted</Tag>}
        onSearch={handleSearch}
        onDropdownVisibleChange={handleDropdownVisibleChange}
      />
    </DimensionFilterContainer>
  );
};

export default DimensionFilter;
